import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation } from 'swiper/modules';
import { ChevronLeftIcon, ChevronRightIcon, StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

interface TestimonialData {
  id: number;
  name: string;
  location: string;
  review: string;
  rating: number;
  image: string;
}

interface TestimonialsProps {
  title?: string;
  subtitle?: string;
  description?: string;
  visibleElements?: Set<string>;
  testimonialsRef?: React.RefObject<HTMLDivElement | null>;
}

// Default testimonials data
const defaultTestimonials: TestimonialData[] = [
  {
    id: 1,
    name: "James & Maria Chen",
    location: "Canada",
    rating: 5,
    review: "Kuber and his family made our honeymoon absolutely magical! The traditional Nepali welcome ceremony brought tears to our eyes. Waking up to sunrise over the Himalayas from our room was unforgettable. The home-cooked dal bhat was the best we had in all of Nepal!",
    image: "https://images.unsplash.com/photo-1521572267360-ee0c2909d518?q=80&w=150&auto=format&fit=crop&ixlib=rb-4.0.3"
  },
  {
    id: 2,
    name: "Anna Müller",
    location: "Germany",
    rating: 5,
    review: "As a solo female traveler, I felt completely safe and welcomed here. Kuber's international experience really shows - he understood exactly what I needed. The village walk he arranged was incredible, and I loved learning to cook momo with the family. This is authentic Nepal at its finest!",
    image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=150&auto=format&fit=crop&ixlib=rb-4.0.3"
  },
  {
    id: 3,
    name: "Robert Williams",
    location: "United Kingdom",
    rating: 5,
    review: "I've stayed in many homestays across Asia, but none compare to Bethanchok. Kuber's role as Ward Chairman gives him unique insights into local life that he shares generously. The terraced fields tour and meeting local artisans was extraordinary. My teenage kids are still talking about it months later!",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=150&auto=format&fit=crop&ixlib=rb-4.0.3"
  },
  {
    id: 4,
    name: "Priya & Arjun Patel",
    location: "India",
    rating: 5,
    review: "Even though we're from India, staying at Bethanchok felt like discovering a completely new culture. The family's warmth transcends all boundaries. Our 8-year-old daughter bonded instantly with the local children, and we learned so much about Nepali traditions. The mountain views were just a bonus to the incredible hospitality!",
    image: "https://images.unsplash.com/photo-1607746882042-944635dfe10e?q=80&w=150&auto=format&fit=crop&ixlib=rb-4.0.3"
  }
];

export default function Testimonials({ 
  title = "Testimonials",
  subtitle = "What Our Guests Say",
  description = "Don't just take our word for it. Here's what previous guests have shared about their experience at Bethanchok Homestay.",
  visibleElements,
  testimonialsRef
}: TestimonialsProps) {
  return (
    <div 
      ref={testimonialsRef}
      data-animate="testimonials"
      className={`py-24 bg-neutral-50 transition-all duration-1000 ease-out transform ${
        visibleElements && visibleElements.has('testimonials')
          ? 'opacity-100 translate-y-0 scale-100'
          : visibleElements ? 'opacity-0 translate-y-20 scale-95' : ''
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl lg:text-center mb-16">
          <h2 className="text-base font-semibold leading-7 text-primary-600">{title}</h2>
          <p className="mt-2 text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
            {subtitle}
          </p>
          <p className="mt-6 text-lg leading-8 text-neutral-600">
            {description}
          </p>
        </div>

        <div className="mx-auto max-w-5xl relative">
          {/* Custom Navigation Buttons - Desktop Only */}
          <div className="hidden lg:block absolute left-0 top-1/2 -translate-y-1/2 -translate-x-16 z-10">
            <button className="testimonials-prev flex items-center justify-center w-12 h-12 bg-primary-600 rounded-full shadow-lg border border-primary-700 hover:bg-primary-700 hover:border-primary-800 transition-all duration-300 group">
              <ChevronLeftIcon className="w-6 h-6 text-white group-hover:text-primary-100" />
            </button>
          </div>
          <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 translate-x-16 z-10">
            <button className="testimonials-next flex items-center justify-center w-12 h-12 bg-primary-600 rounded-full shadow-lg border border-primary-700 hover:bg-primary-700 hover:border-primary-800 transition-all duration-300 group">
              <ChevronRightIcon className="w-6 h-6 text-white group-hover:text-primary-100" />
            </button>
          </div>

          <Swiper
            modules={[Autoplay, Pagination, Navigation]}
            spaceBetween={30}
            slidesPerView={1}
            speed={800}
            autoplay={{
              delay: 6000,
              disableOnInteraction: false,
            }}
            pagination={{
              clickable: true,
              dynamicBullets: true,
            }}
            navigation={{
              prevEl: '.testimonials-prev',
              nextEl: '.testimonials-next',
            }}
            breakpoints={{
              768: {
                slidesPerView: 2,
                spaceBetween: 40,
              },
              1024: {
                slidesPerView: 2,
                spaceBetween: 50,
              },
            }}
            className="testimonials-swiper"
          >
            {defaultTestimonials.map((testimonial) => (
              <SwiperSlide key={testimonial.id}>
                <div className="bg-white p-6 rounded-2xl shadow-soft relative h-full">
                  <div className="absolute -top-3 -left-3">
                    <svg className="h-7 w-7 text-primary-500 transform rotate-180" fill="currentColor" viewBox="0 0 32 32" aria-hidden="true">
                      <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                    </svg>
                  </div>
                  <div className="relative z-10 flex flex-col h-full">
                    {/* Star Rating */}
                    <div className="flex items-center mb-4">
                      <div className="flex items-center mr-2">
                        {[...Array(5)].map((_, index) => (
                          index < testimonial.rating ? (
                            <StarIconSolid key={index} className="h-5 w-5 text-yellow-400" />
                          ) : (
                            <StarIcon key={index} className="h-5 w-5 text-gray-300" />
                          )
                        ))}
                      </div>
                      <span className="text-sm font-medium text-neutral-600">
                        {testimonial.rating}/5 stars
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="text-base text-neutral-600 mb-6 leading-relaxed">
                        "{testimonial.review}"
                      </p>
                    </div>
                    <div className="flex items-center mt-auto">
                      <img 
                        src={testimonial.image} 
                        alt={testimonial.name} 
                        className="h-16 w-16 rounded-full object-cover border-2 border-primary-200 shadow-md"
                      />
                      <div className="ml-4">
                        <h4 className="font-semibold text-neutral-900 text-base">{testimonial.name}</h4>
                        <p className="text-neutral-500 text-sm">{testimonial.location}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </div>
  );
}
